
'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import React from 'react';

// Function to parse Tailwind classes to inline styles
const parseTailwindToInlineStyle = (tailwindClasses: string): React.CSSProperties => {
  const styles: React.CSSProperties = {};
  const classes = tailwindClasses.split(' ').filter(cls => cls.trim());
  
  // Color mapping for standard Tailwind colors
  const colorMap: { [key: string]: string } = {
    // Red colors
    'text-red-50': '#fef2f2', 'text-red-100': '#fee2e2', 'text-red-200': '#fecaca', 'text-red-300': '#fca5a5',
    'text-red-400': '#f87171', 'text-red-500': '#ef4444', 'text-red-600': '#dc2626', 'text-red-700': '#b91c1c',
    'text-red-800': '#991b1b', 'text-red-900': '#7f1d1d',
    // Green colors
    'text-green-50': '#f0fdf4', 'text-green-100': '#dcfce7', 'text-green-200': '#bbf7d0', 'text-green-300': '#86efac',
    'text-green-400': '#4ade80', 'text-green-500': '#22c55e', 'text-green-600': '#16a34a', 'text-green-700': '#15803d',
    'text-green-800': '#166534', 'text-green-900': '#14532d',
    // Blue colors
    'text-blue-50': '#eff6ff', 'text-blue-100': '#dbeafe', 'text-blue-200': '#bfdbfe', 'text-blue-300': '#93c5fd',
    'text-blue-400': '#60a5fa', 'text-blue-500': '#3b82f6', 'text-blue-600': '#2563eb', 'text-blue-700': '#1d4ed8',
    'text-blue-800': '#1e40af', 'text-blue-900': '#1e3a8a',
    // Yellow colors
    'text-yellow-50': '#fefce8', 'text-yellow-100': '#fef3c7', 'text-yellow-200': '#fde68a', 'text-yellow-300': '#fcd34d',
    'text-yellow-400': '#fbbf24', 'text-yellow-500': '#f59e0b', 'text-yellow-600': '#d97706', 'text-yellow-700': '#b45309',
    'text-yellow-800': '#92400e', 'text-yellow-900': '#78350f',
    // Purple colors
    'text-purple-50': '#faf5ff', 'text-purple-100': '#f3e8ff', 'text-purple-200': '#e9d5ff', 'text-purple-300': '#d8b4fe',
    'text-purple-400': '#c084fc', 'text-purple-500': '#a855f7', 'text-purple-600': '#9333ea', 'text-purple-700': '#7c3aed',
    'text-purple-800': '#6b21a8', 'text-purple-900': '#581c87',
    // Gray colors
    'text-gray-50': '#f9fafb', 'text-gray-100': '#f3f4f6', 'text-gray-200': '#e5e7eb', 'text-gray-300': '#d1d5db',
    'text-gray-400': '#9ca3af', 'text-gray-500': '#6b7280', 'text-gray-600': '#4b5563', 'text-gray-700': '#374151',
    'text-gray-800': '#1f2937', 'text-gray-900': '#111827',
    // Black and white
    'text-black': '#000000', 'text-white': '#ffffff',
  };
  
  // Background color mapping
  const bgColorMap: { [key: string]: string } = {
    // Red backgrounds
    'bg-red-50': '#fef2f2', 'bg-red-100': '#fee2e2', 'bg-red-200': '#fecaca', 'bg-red-300': '#fca5a5',
    'bg-red-400': '#f87171', 'bg-red-500': '#ef4444', 'bg-red-600': '#dc2626', 'bg-red-700': '#b91c1c',
    'bg-red-800': '#991b1b', 'bg-red-900': '#7f1d1d',
    // Green backgrounds
    'bg-green-50': '#f0fdf4', 'bg-green-100': '#dcfce7', 'bg-green-200': '#bbf7d0', 'bg-green-300': '#86efac',
    'bg-green-400': '#4ade80', 'bg-green-500': '#22c55e', 'bg-green-600': '#16a34a', 'bg-green-700': '#15803d',
    'bg-green-800': '#166534', 'bg-green-900': '#14532d',
    // Blue backgrounds
    'bg-blue-50': '#eff6ff', 'bg-blue-100': '#dbeafe', 'bg-blue-200': '#bfdbfe', 'bg-blue-300': '#93c5fd',
    'bg-blue-400': '#60a5fa', 'bg-blue-500': '#3b82f6', 'bg-blue-600': '#2563eb', 'bg-blue-700': '#1d4ed8',
    'bg-blue-800': '#1e40af', 'bg-blue-900': '#1e3a8a',
    // Yellow backgrounds
    'bg-yellow-50': '#fefce8', 'bg-yellow-100': '#fef3c7', 'bg-yellow-200': '#fde68a', 'bg-yellow-300': '#fcd34d',
    'bg-yellow-400': '#fbbf24', 'bg-yellow-500': '#f59e0b', 'bg-yellow-600': '#d97706', 'bg-yellow-700': '#b45309',
    'bg-yellow-800': '#92400e', 'bg-yellow-900': '#78350f',
    // Purple backgrounds
    'bg-purple-50': '#faf5ff', 'bg-purple-100': '#f3e8ff', 'bg-purple-200': '#e9d5ff', 'bg-purple-300': '#d8b4fe',
    'bg-purple-400': '#c084fc', 'bg-purple-500': '#a855f7', 'bg-purple-600': '#9333ea', 'bg-purple-700': '#7c3aed',
    'bg-purple-800': '#6b21a8', 'bg-purple-900': '#581c87',
    // Gray backgrounds
    'bg-gray-50': '#f9fafb', 'bg-gray-100': '#f3f4f6', 'bg-gray-200': '#e5e7eb', 'bg-gray-300': '#d1d5db',
    'bg-gray-400': '#9ca3af', 'bg-gray-500': '#6b7280', 'bg-gray-600': '#4b5563', 'bg-gray-700': '#374151',
    'bg-gray-800': '#1f2937', 'bg-gray-900': '#111827',
    // Black and white backgrounds
    'bg-black': '#000000', 'bg-white': '#ffffff',
  };
  
  classes.forEach(cls => {
    // Handle text colors - arbitrary values first
    if (cls.startsWith('text-[') && cls.endsWith(']')) {
      const color = cls.match(/text-\[([^\]]+)\]/)?.[1];
      if (color) {
        styles.color = color;
      }
    } else if (cls.startsWith('text-') && colorMap[cls]) {
      // Handle standard Tailwind text colors
      styles.color = colorMap[cls];
    }
    
    // Handle background colors - arbitrary values first
    if (cls.startsWith('bg-[') && cls.endsWith(']')) {
      const color = cls.match(/bg-\[([^\]]+)\]/)?.[1];
      if (color) {
        styles.backgroundColor = color;
      }
    } else if (cls.startsWith('bg-') && bgColorMap[cls]) {
      // Handle standard Tailwind background colors
      styles.backgroundColor = bgColorMap[cls];
    }
    
    // Handle border colors
    if (cls.startsWith('border-[') && cls.endsWith(']')) {
      const color = cls.match(/border-\[([^\]]+)\]/)?.[1];
      if (color) {
        styles.borderColor = color;
        styles.borderWidth = '1px';
        styles.borderStyle = 'solid';
      }
    }
    
    // Handle font weight
    if (cls === 'font-bold') styles.fontWeight = 'bold';
    if (cls === 'font-semibold') styles.fontWeight = '600';
    if (cls === 'font-medium') styles.fontWeight = '500';
    if (cls === 'font-normal') styles.fontWeight = 'normal';
    if (cls === 'font-light') styles.fontWeight = '300';
    if (cls === 'font-thin') styles.fontWeight = '100';
    
    // Handle font style
    if (cls === 'italic') styles.fontStyle = 'italic';
    
    // Handle text decoration
    if (cls === 'underline') styles.textDecoration = 'underline';
    if (cls === 'line-through') styles.textDecoration = 'line-through';
    
    // Handle text alignment
    if (cls === 'text-left') styles.textAlign = 'left';
    if (cls === 'text-center') styles.textAlign = 'center';
    if (cls === 'text-right') styles.textAlign = 'right';
    if (cls === 'text-justify') styles.textAlign = 'justify';
  });
  
  return styles;
};
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  ColumnSizingState,
  ColumnOrderState,
} from '@tanstack/react-table';
import type { ColumnConfig, DataGridProps } from '../types';
import { filterFns } from '../utils/filterFunctions';
import { renderTemplate } from '../utils/templateRenderer';
import { parseGridDate, formatGridDate } from '../utils/dateUtils';
import { StackedCellRenderer } from '../components/StackedCellRenderer';

export const useDataGrid = ({
  dataSource,
  columns,
  gridConfig,
  onRowSelect,
  onRowClick,
  onPaginationChange,
  manualPagination = false,
  pageCount,
  pageIndex: externalPageIndex,
  manualFiltering = false,
  onColumnFiltersChange,
  manualSorting = false,
  onSortingChange,
}: DataGridProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Server-side filtering / sorting: report every change after mount so the page can
  // re-query. Refs keep a parent's fresh callback without re-firing the effect.
  const filtersCb = useRef(onColumnFiltersChange);
  filtersCb.current = onColumnFiltersChange;
  const sortingCb = useRef(onSortingChange);
  sortingCb.current = onSortingChange;
  const filtersMounted = useRef(false);
  const sortingMounted = useRef(false);
  useEffect(() => {
    if (!filtersMounted.current) { filtersMounted.current = true; return; }
    filtersCb.current?.(columnFilters as { id: string; value: any }[]);
  }, [columnFilters]);
  useEffect(() => {
    if (!sortingMounted.current) { sortingMounted.current = true; return; }
    sortingCb.current?.(sorting as { id: string; desc: boolean }[]);
  }, [sorting]);
  // Initialize column visibility from column config
  const initialColumnVisibility = useMemo(() => {
    const visibility: VisibilityState = {};
    columns.forEach((col) => {
      if (col.field && col.visible === false) {
        visibility[col.field] = false;
      }
    });
    return visibility;
  }, []);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: externalPageIndex ?? 0,
    pageSize: gridConfig.defaultPageSize || gridConfig.minPageSizes?.[0] || 10,
  });

  // Sync internal pagination state with external pageIndex when it changes (for server-side pagination)
  useEffect(() => {
    if (manualPagination && externalPageIndex !== undefined && externalPageIndex !== pagination.pageIndex) {
      setPagination(prev => ({
        ...prev,
        pageIndex: externalPageIndex
      }));
    }
  }, [externalPageIndex, manualPagination]);

  // Handle pagination change and notify parent
  const handlePaginationChange = useCallback((updater: any) => {
    setPagination((prev) => {
      const newPagination = typeof updater === 'function' ? updater(prev) : updater;
      if (onPaginationChange) {
        onPaginationChange(newPagination);
      }
      return newPagination;
    });
  }, [onPaginationChange]);

  // Handle column order strictly based on provided nested columns order
  const calculatedColumnOrder = useMemo(() => {
    if (!columns || columns.length === 0) return [];

    const order: string[] = [];

    // Add selection column first if row selection is enabled
    if ((gridConfig as any).enableRowSelection || (gridConfig as any).enableMultiRowSelection) {
      order.push('select');
    }

    const pushField = (field?: string) => {
      if (field && !order.includes(field)) order.push(field);
    };

    columns.forEach(col => {
      if (col.subColumns && col.subColumns.length > 0) {
        col.subColumns.forEach(sub => pushField(sub.field));
      } else {
        pushField(col.field);
      }
    });

    return order;
  }, [columns, gridConfig]);

  // Update column order only when the calculated order actually changes
  useEffect(() => {
    const currentOrderString = columnOrder.join(',');
    const newOrderString = calculatedColumnOrder.join(',');

    if (currentOrderString !== newOrderString) {
      setColumnOrder(calculatedColumnOrder);
    }
  }, [calculatedColumnOrder, columnOrder]);

  // One cell function per column id, kept for the life of the grid.
  //
  // flexRender() mounts a function `cell` as a React COMPONENT. Callers usually pass a
  // fresh `columns` array on every render, which rebuilt `cell` as a new function, so
  // React saw a different component type and unmounted/remounted every cell each time
  // the parent's state changed. An <input> inside a cellRenderer therefore lost focus
  // after every keystroke. The stable function reads the latest column config and
  // renderer through refs, so cells still re-render with current values.
  const latestColsRef = useRef(new Map<string, any>());
  const renderCellRef = useRef<(value: any, column: ColumnConfig, rowData: any) => any>(() => null);
  const stableCellsRef = useRef(new Map<string, (ctx: any) => any>());
  const stableCell = (key: string, col: any) => {
    latestColsRef.current.set(key, col);
    let fn = stableCellsRef.current.get(key);
    if (!fn) {
      fn = ({ getValue, row }: any) => renderCellRef.current(getValue(), latestColsRef.current.get(key), row.original);
      stableCellsRef.current.set(key, fn);
    }
    return fn;
  };

  // Transform columns to TanStack Table format
  const tableColumns = useMemo<ColumnDef<any>[]>(() => {
    const transformedColumns: ColumnDef<any>[] = [];

    const toColumnDef = (col: any): ColumnDef<any> => {
      if (col.subColumns && col.subColumns.length > 0) {
        // Group or subgroup node
        return {
          id: col.field || col.columnName,
          header: col.columnName,
          columns: col.subColumns.map((child: any) => toColumnDef(child)),
        } as ColumnDef<any>;
      }
      // Leaf column
      // Determine filter function name - use string references for TanStack Table lookup
      let filterFnName: string = col.allowAdvancedFilter ? 'advancedFilter' : 'uniqueValuesFilter';

      // For columns with custom filterField/filterValue, register a per-column custom filter
      if (col.filterField || col.filterValue) {
        filterFnName = `custom_${col.field || col.columnName}`;
      }

      const stableKey = col.field || col.columnName;
      return {
        id: stableKey || `col-${Math.random().toString(36).slice(2)}`,
        accessorKey: col.field,
        header: col.columnName,
        size: col.width ? parseInt(col.width) : undefined,
        // A column with neither field nor columnName has no stable id to key on, so it
        // keeps the old per-render function.
        cell: stableKey
          ? stableCell(stableKey, col)
          : ({ getValue, row }) => renderCellRef.current(getValue(), col, row.original),
        enableSorting: true,
        // Date columns arrive as DD-MM-YYYY strings; the default alphabetical sort
        // would order them by day-of-month.
        ...(col.columnType === 'date' ? {
          sortingFn: (a: any, b: any, columnId: string) => {
            const da = parseGridDate(a.getValue(columnId))?.getTime();
            const db = parseGridDate(b.getValue(columnId))?.getTime();
            if (da == null && db == null) return 0;
            if (da == null) return -1;
            if (db == null) return 1;
            return da - db;
          },
        } : {}),
        enableColumnFilter: col.filter !== false,
        filterFn: filterFnName as any,
      } as ColumnDef<any>;
    };

    // Add selection column only if row selection is enabled in grid config
    if ((gridConfig as any).enableRowSelection || (gridConfig as any).enableMultiRowSelection) {
      transformedColumns.push({
        id: 'select',
        /*
         * A row the table refuses to select must LOOK refused.
         *
         * When `isRowSelectable` returns false for a row, table-core's
         * mutateRowIsSelected() drops the write, so `row.toggleSelected()` is a
         * no-op and `getIsSelected()` stays false. This input was rendered
         * unconditionally enabled: clicking it changed no state, React therefore
         * never re-rendered it, and the browser's own tick stayed on screen — a
         * checkbox that appears checked while nothing is selected, and an
         * onRowSelect that never fires. Whole toolbars looked broken because of it.
         *
         * `disabled` makes the refusal visible instead of silent, and keeps the
         * DOM in step with the table's state.
         */
        header: ({ table }) => {
          const anySelectable = table.getRowModel().rows.some((row) => row.getCanSelect());
          return (
            <input
              type="checkbox"
              checked={table.getIsAllPageRowsSelected()}
              disabled={!anySelectable}
              title={anySelectable ? undefined : 'No selectable rows'}
              onChange={(e) => {
                table.toggleAllPageRowsSelected(e.target.checked);
              }}
              className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
            />
          );
        },
        cell: ({ row }) => {
          const canSelect = row.getCanSelect();
          return (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              disabled={!canSelect}
              title={canSelect ? undefined : 'This row cannot be selected'}
              onChange={(e) => {
                row.toggleSelected(e.target.checked);
              }}
              className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 50,
        minSize: 50,
      });
    }

    columns.forEach((col) => {
      // Include all columns - visibility is handled by columnVisibility state
      transformedColumns.push(toColumnDef(col));
    });

    return transformedColumns;
  }, [columns]);

  // Build filter functions registry - includes standard + per-column custom filters
  const allFilterFns = useMemo(() => {
    const fns: Record<string, any> = { ...filterFns };

    // Register custom per-column filter functions for columns with filterField/filterValue
    columns.forEach((col: any) => {
      if (col.filterField || col.filterValue) {
        const key = `custom_${col.field || col.columnName}`;
        const customFn: any = (row: any, columnId: string, filterVal: any) => {
          if (filterVal === undefined || filterVal === null) return true;
          if (Array.isArray(filterVal) && filterVal.length === 0) return true;

          let cellStr: string;
          if (col.filterValue) {
            cellStr = col.filterValue(row.original);
          } else if (col.filterField) {
            const val = row.original[col.filterField];
            cellStr = val === null || val === undefined ? '' : String(val);
          } else {
            const val = row.getValue(columnId);
            cellStr = val === null || val === undefined ? '' : String(val);
          }

          if (Array.isArray(filterVal)) return filterVal.includes(cellStr);
          if (typeof filterVal === 'string') {
            if (filterVal === '') return true;
            return cellStr.toLowerCase().includes(filterVal.toLowerCase());
          }
          return true;
        };
        customFn.autoRemove = (val: any) => !val || (Array.isArray(val) && val.length === 0) || val === '';
        fns[key] = customFn;
      }
    });

    return fns;
  }, [columns]);

  const table = useReactTable({
    data: dataSource,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      columnSizing,
      columnOrder,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: handlePaginationChange,
    enableFilters: true,
    enableColumnFilters: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: manualPagination,
    pageCount: manualPagination ? pageCount : undefined,
    manualFiltering,
    manualSorting,
    enableRowSelection: (gridConfig as any).enableRowSelection || (gridConfig as any).enableMultiRowSelection || false,
    enableMultiRowSelection: (gridConfig as any).enableMultiRowSelection || false,
    enableColumnResizing: gridConfig.enableColumnResizing || false,
    columnResizeMode: 'onChange',
    filterFns: allFilterFns,
  });

  // Get selected rows
  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows.map(row => row.original);
  }, [rowSelection]);

  // Handle row selection callback in useEffect to avoid setState during render
  useEffect(() => {
    if (onRowSelect) {
      onRowSelect(selectedRows);
    }
  }, [selectedRows, onRowSelect]);

  // Helper function to format cell value with N/A handling
  const formatCellValue = (value: any): string => {
    // Check for null, undefined, empty string
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    // Check for whitespace-only strings
    if (typeof value === 'string' && value.trim() === '') {
      return 'N/A';
    }
    return value;
  };

  const renderCellContent = useCallback((value: any, column: ColumnConfig, rowData: any) => {
    // If custom cellRenderer is provided, use it directly
    if (column.cellRenderer) {
      return column.cellRenderer(rowData);
    }

    // If stackedFields is provided, use StackedCellRenderer
    if (column.stackedFields && column.stackedFields.length > 0) {
      return <StackedCellRenderer rowData={rowData} stackedFields={column.stackedFields} />;
    }

    // Apply conditional styling
    let className = column.column_css_class || '';
    let inlineStyle: React.CSSProperties = {};
    
    // Handle both conditionalStyles (legacy) and conditionalFormats (unified approach)
    if (column.conditionalStyles) {
      column.conditionalStyles.forEach((style) => {
        try {
          // Simple evaluation of conditional styles
          const condition = style.if.replace(/row\./g, 'rowData.');
          
          if (eval(condition)) {
            console.log('Condition matched! Applying class:', style.className);
            className += ` ${style.className}`;
            
            // Also apply inline styles for better compatibility
            const styleObj = parseTailwindToInlineStyle(style.className);
            inlineStyle = { ...inlineStyle, ...styleObj };
          }
        } catch (e) {
          // Silently ignore invalid conditional styles
        }
      });
    }
    
    // Process conditionalFormats (unified approach)
    const conditionalFormats = column.conditionalFormats || column.conditions || [];
    if (conditionalFormats.length > 0) {
      conditionalFormats.forEach((format) => {
        try {
          let conditionMet = false;
          const cellValue = value;
          const compareValue1 = format.value1;
          const compareValue2 = format.value2;
          
          // Evaluate condition based on type
          switch (format.condition) {
            case 'equals':
              conditionMet = cellValue == compareValue1;
              break;
            case 'notEquals':
              conditionMet = cellValue != compareValue1;
              break;
            case 'contains':
              conditionMet = String(cellValue).toLowerCase().includes(String(compareValue1).toLowerCase());
              break;
            case 'notContains':
              conditionMet = !String(cellValue).toLowerCase().includes(String(compareValue1).toLowerCase());
              break;
            case 'startsWith':
              conditionMet = String(cellValue).toLowerCase().startsWith(String(compareValue1).toLowerCase());
              break;
            case 'endsWith':
              conditionMet = String(cellValue).toLowerCase().endsWith(String(compareValue1).toLowerCase());
              break;
            case 'greaterThan':
              conditionMet = Number(cellValue) > Number(compareValue1);
              break;
            case 'greaterThanOrEqual':
              conditionMet = Number(cellValue) >= Number(compareValue1);
              break;
            case 'lessThan':
              conditionMet = Number(cellValue) < Number(compareValue1);
              break;
            case 'lessThanOrEqual':
              conditionMet = Number(cellValue) <= Number(compareValue1);
              break;
            case 'between':
              conditionMet = Number(cellValue) >= Number(compareValue1) && Number(cellValue) <= Number(compareValue2);
              break;
            case 'notBetween':
              conditionMet = Number(cellValue) < Number(compareValue1) || Number(cellValue) > Number(compareValue2);
              break;
          }
          
          if (conditionMet) {
            console.log('Condition matched! Applying class:', format.cssClass);
            className += ` ${format.cssClass}`;
            
            // Also apply inline styles for better compatibility
            const styleObj = parseTailwindToInlineStyle(format.cssClass);
            inlineStyle = { ...inlineStyle, ...styleObj };
          }
        } catch (error) {
          // Silently ignore errors in conditional formatting
        }
      });
    }

    // Alignment
    const alignmentClass = column.alignment === 'center' ? 'text-center' : 
                          column.alignment === 'right' ? 'text-right' : 'text-left';

    const cellClassName = `${alignmentClass} ${className}`.trim();

    switch (column.columnType) {
      case 'currency':
        if (value === null || value === undefined || value === '') {
          return <span className={cellClassName} style={inlineStyle}>N/A</span>;
        }
        // INR is the default: this is an Andhra Pradesh government application and every
        // amount in it is rupees. The previous ternary fell through to '$' + en-US
        // whenever a column omitted displayFormat, so grids rendered sanction amounts as
        // US dollars with Western grouping ($371,420,000 rather than ₹37,14,20,000).
        // 'DOLLAR' stays available but must now be asked for explicitly.
        const symbol = column.displayFormat === 'DOLLAR' ? '$' : '₹';
        const locale = column.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
        return <span className={cellClassName} style={inlineStyle}>{symbol}{value?.toLocaleString(locale)}</span>;

      case 'percentage':
        if (value === null || value === undefined || value === '') {
          return <span className={cellClassName} style={inlineStyle}>N/A</span>;
        }
        return <span className={cellClassName} style={inlineStyle}>{value}%</span>;
        
      case 'rating':
        return (
          <div className={cellClassName} style={inlineStyle}>
            {[...Array(5)].map((_, i) => (
              <span key={i} className={i < value ? 'text-yellow-400' : 'text-gray-300'}>
                ★
              </span>
            ))}
          </div>
        );
        
      case 'boolean':
        return (
          <span className={`px-2 py-1 rounded text-sm ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        );
        
      case 'date': {
        // formatGridDate understands the DD-MM-YYYY strings the APIs return; falling
        // back to the raw value keeps an unrecognised date visible instead of
        // printing "Invalid Date" over it.
        const formatted = formatGridDate(value);
        return (
          <span className={cellClassName} suppressHydrationWarning={true}>
            {formatted ?? (value ? String(value) : 'N/A')}
          </span>
        );
      }
        
      case 'button':
        return (
          <button
            onClick={() => column.actionEventEmiter?.(rowData)}
            className="px-3 py-1 bg-violet-500 text-white rounded hover:bg-violet-600 text-sm whitespace-nowrap"
          >
            Action
          </button>
        );
        
      case 'individual_selection':
        return (
          <select 
            className="border rounded px-2 py-1 text-sm pr-8"
            onChange={(e) => column.actionEventEmiter?.(rowData)}
          >
            <option value="">Select...</option>
            <option value="option1">Option 1</option>
            <option value="option2">Option 2</option>
          </select>
        );
        
      case 'multi_selection':
        return (
          <div className="flex gap-1">
            <input type="checkbox" className="w-4 h-4" />
            <input type="checkbox" className="w-4 h-4" />
          </div>
        );
        
      case 'image':
        const imageSize = column.imageSize || 'md';
        const imageShape = column.imageShape || 'circle';
        const fallbackText = column.fallbackText || (rowData[column.field || 'name'] || 'U').charAt(0).toUpperCase();
        const showFallbackIcon = column.showFallbackIcon !== false;
        
        const sizeClasses = {
          xs: 'w-6 h-6 text-xs',
          sm: 'w-8 h-8 text-sm', 
          md: 'w-10 h-10 text-base',
          lg: 'w-12 h-12 text-lg',
          xl: 'w-16 h-16 text-xl'
        };
        
        const shapeClasses = {
          circle: 'rounded-full',
          rounded: 'rounded-lg',
          square: 'rounded-none'
        };
        
        return (
          <div className={`flex items-center ${cellClassName}`}>
            {value ? (
              <img
                src={value}
                alt={fallbackText}
                className={`${sizeClasses[imageSize]} ${shapeClasses[imageShape]} object-cover border border-gray-200`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`${sizeClasses[imageSize]} ${shapeClasses[imageShape]} bg-gray-300 flex items-center justify-center text-gray-600 font-semibold border border-gray-200 ${value ? 'hidden' : 'flex'}`}
              style={{ display: value ? 'none' : 'flex' }}
            >
              {showFallbackIcon ? (
                <i className="ri-user-line"></i>
              ) : (
                fallbackText
              )}
            </div>
          </div>
        );
        
      case 'template':
        if (column.template) {
          return (
            <div className={cellClassName}>
              {renderTemplate(column.template, rowData)}
            </div>
          );
        }
        return <span className={cellClassName} style={inlineStyle}>{formatCellValue(value)}</span>;
        
      default:
        return <span className={cellClassName} style={inlineStyle}>{formatCellValue(value)}</span>;
    }
  }, []);
  // Cells are rendered after this hook returns, so the stable cell functions above
  // always see the current renderer.
  renderCellRef.current = renderCellContent;

  // Direct column filter setter - bypasses TanStack's setFilterValue pipeline
  const setColumnFilter = useCallback((columnId: string, value: any) => {
    setColumnFilters(prev => {
      // Remove existing filter for this column
      const filtered = prev.filter(f => f.id !== columnId);
      // If value is empty/null/undefined, just remove the filter
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        return filtered;
      }
      // Add the new filter
      return [...filtered, { id: columnId, value }];
    });
  }, []);

  return {
    table,
    sorting,
    columnFilters,
    columnVisibility,
    rowSelection,
    globalFilter,
    pagination,
    selectedRows,
    setGlobalFilter,
    setPagination,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    setColumnFilter,
  };
};
